import React, { useRef, useMemo, useEffect } from 'react';
import { DoubleSide, InstancedMesh, Object3D } from 'three';
import * as THREE from 'three';

// Highly realistic tightly packed powder simulation
function NaphthalenePowder({ tubeRadius, substanceHeight }: { tubeRadius: number; substanceHeight: number }) {
  const instancedMeshRef = useRef<InstancedMesh>(null);
  const dummy = useMemo(() => new Object3D(), []);
  
  useEffect(() => {
    if (!instancedMeshRef.current) return;
    
    const count = 3000; 
    let idx = 0;
    
    // Fill the hemisphere bottom and the cylinder body
    for (let i = 0; i < count; i++) {
        let x, y, z;
        while (true) {
          x = (Math.random() - 0.5) * 2 * (tubeRadius - 0.008);
          z = (Math.random() - 0.5) * 2 * (tubeRadius - 0.008);
          y = (Math.random() * (substanceHeight + tubeRadius)) - tubeRadius;
          
          if (y < 0) {
            if (x * x + y * y + z * z <= Math.pow(tubeRadius - 0.008, 2)) break;
          } else {
            if (x * x + z * z <= Math.pow(tubeRadius - 0.008, 2)) break;
          }
        }
        
        dummy.position.set(x, y, z);
        dummy.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
        // Larger uneven chunks to form packed mass
        const s = 0.003 + Math.random() * 0.005;
        dummy.scale.set(s, s, s);
        dummy.updateMatrix();
        instancedMeshRef.current.setMatrixAt(idx++, dummy.matrix);
    }
    
    instancedMeshRef.current.instanceMatrix.needsUpdate = true;
  }, [substanceHeight, tubeRadius, dummy]);
  
  return (
    <instancedMesh 
      ref={instancedMeshRef} 
      args={[undefined, undefined, 3000]}
      position={[0, 0, 0]}
      renderOrder={2}
    >
      <icosahedronGeometry args={[1, 0]} />
      <meshStandardMaterial 
        color="#f3f3f3" 
        roughness={1.0} 
        flatShading={true}
      />
    </instancedMesh>
  );
}

interface BoilingTubeProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  substanceLevel?: number; // 0 to 1
  stateOfMatter?: 'Solid' | 'Solid+Liquid' | 'Liquid';
}

export function BoilingTube({ 
  position = [0, 0, 0], 
  rotation = [0, 0, 0], 
  scale = 1, 
  substanceLevel = 0.4,
  stateOfMatter = 'Solid'
}: BoilingTubeProps) {
  // Realistic Boiling Tube dimensions mathematically matched to the clamp jaws
  // The clamp jaw radius is 0.055, so the tube matches this perfectly.
  const rad = 0.055;
  const height = 0.85; // Corrected height to accurately represent boiling tube aspect ratio 

  const fillLevel = Math.max(0, Math.min(1, substanceLevel));
  const substanceHeight = fillLevel * height;

  // Determine material color/look based on state of matter for Naphthalene liquid phase
  let substanceColor = "#ffffff";
  let opacity = 1;
  let roughness = 0.9;
  let transmission = 0.0;
  let clearcoat = 0.0;

  if (stateOfMatter === 'Liquid') {
    substanceColor = "#f0f8ff"; // clearer liquid with slight tint
    opacity = 0.85;
    roughness = 0.0;
    transmission = 0.95;
    clearcoat = 1.0;
  } else if (stateOfMatter === 'Solid+Liquid') {
    substanceColor = "#fdfdfd";
    opacity = 0.95;
    roughness = 0.4;
    transmission = 0.6;
    clearcoat = 0.5;
  }

  const glassMaterial = (
    <meshPhysicalMaterial
      color="#ffffff"
      transparent={true}
      opacity={0.3}
      roughness={0.05}
      metalness={0.1}
      transmission={0.9} // Slightly lowered transmission so it remains visible inside the water
      ior={1.52} // Increased IOR slightly to ensure refraction boundary against water (1.33)
      thickness={0.005}
      side={DoubleSide}
      depthWrite={true} // MUST be true for nested transparent objects (tube inside water)
    />
  );

  return (
    <group position={position} rotation={rotation} scale={scale}>
      {/* Glass Body (Centered exactly so grip coordinates match real world center) */}
      <group position={[0, -height/2, 0]}>
        
        {/* Main Cylinder */}
        <mesh position={[0, height / 2, 0]} castShadow renderOrder={3}>
          <cylinderGeometry args={[rad, rad, height, 32, 1, true]} />
          {glassMaterial}
        </mesh>
        
        {/* Glass Curved Bottom */}
        <mesh position={[0, 0, 0]} renderOrder={3}>
          {/* Half sphere facing downward */}
          <sphereGeometry args={[rad, 32, 16, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2]} />
          {glassMaterial}
        </mesh>

        {/* Top Rim (Restored and horizontally rotated to serve as an actual lip, fixing the vertical circle) */}
        <mesh position={[0, height, 0]} rotation={[Math.PI / 2, 0, 0]} renderOrder={3}>
          <torusGeometry args={[rad + 0.003, 0.012, 16, 32]} />
          {glassMaterial}
        </mesh>

        {/* Substance Fill (Naphthalene) */}
        {fillLevel > 0 && (
          <group position={[0, 0, 0]}>
            {stateOfMatter === 'Solid' ? (
              <NaphthalenePowder tubeRadius={rad} substanceHeight={substanceHeight} />
            ) : (
              <group>
                <mesh position={[0, 0, 0]} renderOrder={1}>
                  <sphereGeometry args={[rad - 0.008, 32, 16, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2]} />
                  <meshPhysicalMaterial 
                    color={substanceColor}
                    transparent={opacity < 1}
                    opacity={opacity}
                    roughness={roughness}
                    transmission={transmission}
                    clearcoat={clearcoat}
                    ior={1.4}
                  />
                </mesh>
                <mesh position={[0, substanceHeight / 2, 0]} renderOrder={1}>
                  <cylinderGeometry args={[rad - 0.008, rad - 0.008, substanceHeight, 32]} />
                  <meshPhysicalMaterial 
                    color={substanceColor}
                    transparent={opacity < 1}
                    opacity={opacity}
                    roughness={roughness}
                    transmission={transmission}
                    clearcoat={clearcoat}
                    ior={1.4}
                  />
                </mesh>
                <mesh position={[0, substanceHeight, 0]} renderOrder={1}>
                  <cylinderGeometry args={[rad - 0.006, rad - 0.008, 0.01, 32]} />
                  <meshPhysicalMaterial 
                    color={substanceColor}
                    transparent={opacity < 1}
                    opacity={opacity}
                    roughness={roughness}
                    transmission={transmission}
                    clearcoat={0.2}
                    side={DoubleSide}
                  />
                </mesh>
              </group>
            )}
          </group>
        )}
      </group>
    </group>
  );
}
